/**
 * Tester auth context + hook (uat-portal-suite).
 *
 * Story: epic-uat-portal-001-8.
 *
 * Split out of `TesterAuthProvider.tsx` so the provider file exports ONLY a
 * component (keeps `react-refresh/only-export-components` happy under the
 * suite's `--max-warnings 0` lint gate). Consumers import `useTesterAuth` from
 * here; the provider imports `TesterAuthContext`.
 */

import { createContext, useContext } from 'react';

/** A standalone lightweight UAT-portal tester account (NOT a DAO member). */
export interface TesterAccount {
  id: string;
  email: string;
  displayName: string;
}

export interface TesterAuthContextValue {
  /** The signed-in tester account, or null when unauthenticated. */
  account: TesterAccount | null;
  /** True when a tester session is active. */
  isAuthed: boolean;
  /**
   * Establish a session after a successful redeem / verify-link: stores the
   * session token (in-memory) and the account in context.
   */
  login: (account: TesterAccount, sessionToken: string) => void;
  /** Clear the session (token + account). */
  logout: () => void;
}

export const TesterAuthContext = createContext<TesterAuthContextValue | null>(null);

/**
 * Access the tester auth context. Throws when used outside `TesterAuthProvider`
 * so a wiring mistake fails loudly rather than silently reporting "logged out".
 */
export function useTesterAuth(): TesterAuthContextValue {
  const ctx = useContext(TesterAuthContext);
  if (ctx === null) {
    throw new Error('useTesterAuth must be used within a TesterAuthProvider');
  }
  return ctx;
}
